import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Check, Loader2, Save, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ExpandableText from "./ExpandableText";

const INPUT = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none";
const LABEL = "block text-[10px] uppercase tracking-widest text-ivory/65 mb-1";

// ---- Taux de réponse aux invitations d'avis (courriels post-livraison) ----
function AdminReviewKit() {
  // Kit « récolter des avis Google » (demande du propriétaire, 2026-10-28) :
  // le lien d'avis direct + un message FR/EN prêts à copier (texto, Messenger)
  const [kit, setKit] = useState(null);
  useEffect(() => { api.get("/admin/review-kit").then((r) => setKit(r.data)).catch(() => setKit({})); }, []);
  if (!kit) return null;
  const copier = (txt, quoi) => {
    navigator.clipboard.writeText(txt).then(() => toast.success(`${quoi} copié !`))
      .catch(() => toast.error("Copie impossible — sélectionnez le texte à la main."));
  };
  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-6 mb-8" data-testid="admin-review-kit">
      <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Récolter plus d'avis Google</p>
      <p className="text-xs text-ivory/55 mb-4">{kit.astuce}</p>
      {kit.place_id_configure ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input readOnly value={kit.lien} data-testid="review-kit-lien"
              className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs text-ivory/80" />
            <button type="button" onClick={() => copier(kit.lien, "Lien d'avis")} data-testid="review-kit-copier-lien"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs rounded-full bg-gold text-ink font-semibold px-3 py-2 hover:bg-champagne transition-colors">
              Copier le lien
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {[["fr", "Message français", kit.message_fr], ["en", "Message anglais", kit.message_en]].map(([k, titre, msg]) => (
              <div key={k} className="bg-ink border border-ivory/10 rounded-lg p-3" data-testid={`review-kit-msg-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45">{titre}</p>
                  <button type="button" onClick={() => copier(msg, `Message ${k === "fr" ? "français" : "anglais"}`)}
                    data-testid={`review-kit-copier-${k}`}
                    className="text-[11px] rounded-full border border-gold/40 text-gold px-2.5 py-1 hover:bg-gold hover:text-ink transition-colors">
                    Copier
                  </button>
                </div>
                <p className="text-xs text-ivory/70 leading-relaxed">{msg}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-amber-300/80">Le Place ID Google n'est pas configuré — le lien apparaîtra ici dès qu'il le sera.</p>
      )}
    </div>
  );
}

function AdminReviewStats() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/admin/review-stats").then((r) => setSt(r.data)).catch(() => setSt({ invitees: 0, cliquees: 0, deposees: 0, relances: 0 }));
  }, []);
  if (!st) return null;
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
  const rows = [
    { label: "Invitations envoyées", value: st.invitees, testid: "review-stat-invitees" },
    { label: `Liens cliqués — ${pct(st.cliquees, st.invitees)} %`, value: st.cliquees, testid: "review-stat-cliquees" },
    { label: `Avis déposés — ${pct(st.deposees, st.invitees)} %`, value: st.deposees, testid: "review-stat-deposees" },
    { label: "Relances douces envoyées", value: st.relances, testid: "review-stat-relances" },
  ];
  const max = Math.max(st.invitees, 1);
  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-6 mb-8" data-testid="admin-review-stats">
      <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Taux de réponse aux invitations d'avis</p>
      <p className="text-xs text-ivory/55 mb-4">
        Courriels automatiques post-livraison (3 jours) puis relance douce unique (7 jours plus tard si aucun clic).
      </p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.testid} data-testid={r.testid}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-ivory/70">{r.label}</span>
              <strong className="text-champagne">{r.value}</strong>
            </div>
            <div className="h-1.5 bg-ink rounded-full overflow-hidden">
              <div className="h-full bg-gold/80 rounded-full transition-all duration-500"
                style={{ width: `${Math.max((r.value / max) * 100, r.value ? 3 : 0)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Modération des avis PRODUITS (déposés sur les fiches, achat vérifié) ----
function AdminProductReviews() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/product-reviews").then((r) => setData(r.data)).catch(() => setData({ reviews: [], pending: 0 }));
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    setBusy(`ok-${r.id}`);
    try {
      const res = await api.post(`/admin/product-reviews/${r.id}/approve`);
      toast.success(res.data.approved ? "Avis approuvé — visible sur la fiche produit" : "Avis retiré de la fiche produit");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Supprimer définitivement l'avis de ${r.name} sur « ${r.product_name} » ?`)) return;
    setBusy(`rm-${r.id}`);
    try {
      await api.delete(`/admin/product-reviews/${r.id}`);
      toast.success("Avis supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!data) return null;
  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-6 mb-8" data-testid="admin-product-reviews">
      <p className="text-[11px] uppercase tracking-widest text-gold mb-1">
        Avis produits — modération {data.pending > 0 && <span className="text-amber-400">({data.pending} en attente)</span>}
      </p>
      <p className="text-xs text-ivory/55 mb-5">
        Déposés par les clients sur les fiches produits (achat vérifié). Un avis n'est visible sur le site qu'après votre approbation.
      </p>
      {data.reviews.length === 0 ? (
        <p className="text-ivory/45 text-sm" data-testid="admin-product-reviews-empty">Aucun avis produit pour le moment.</p>
      ) : (
        <ul className="space-y-3">
          {data.reviews.map((r) => (
            <li key={r.id} data-testid={`admin-product-review-${r.id}`}
              className={`border rounded-lg p-4 ${r.approved ? "border-ivory/10" : "border-amber-400/40 bg-amber-400/5"}`}>
              <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                <span className="flex gap-0.5">
                  {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "fill-gold text-gold" : "text-gold/30"}`} />)}
                </span>
                <span className="text-sm font-semibold text-champagne">{r.name}</span>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5">
                    <BadgeCheck size={10} /> Achat vérifié
                  </span>
                )}
                <Link to={`/produit/${r.product_id}`} className="text-xs text-gold hover:underline">{r.product_name}</Link>
                <span className="text-[11px] text-ivory/40 ml-auto">{new Date(r.created_at).toLocaleDateString("fr-CA")}</span>
                {!r.approved && <span className="text-[10px] uppercase tracking-wider text-amber-400">En attente</span>}
              </div>
              <p className="text-sm text-ivory/75 mb-3">{r.text}</p>
              <div className="flex gap-2">
                <button onClick={() => approve(r)} disabled={busy === `ok-${r.id}`}
                  data-testid={`admin-product-review-approve-${r.id}`}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    r.approved ? "border border-ivory/20 text-ivory/60 hover:text-gold" : "bg-emerald-500 text-ink hover:bg-emerald-400"}`}>
                  <Check size={13} /> {r.approved ? "Retirer du site" : "Approuver"}
                </button>
                <button onClick={() => remove(r)} disabled={busy === `rm-${r.id}`}
                  data-testid={`admin-product-review-delete-${r.id}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors">
                  <Trash2 size={13} /> Supprimer
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- Avis Google Business Profile (demande du propriétaire) : TOUS les avis de la
// fiche, réponses publiées DIRECTEMENT sur Google — nécessite la connexion OAuth
// (bouton ci-dessous) ET l'approbation de l'API par Google (formulaire d'accès) ----
function AdminGoogleBusinessReviews() {
  const [st, setSt] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [guide, setGuide] = useState(false);

  const loadReviews = () => api.get("/admin/gbp/reviews").then((res) => {
    setData(res.data);
    setErr("");
    const d = {};
    (res.data.reviews || []).forEach((rv) => { d[rv.review_id] = rv.reply || ""; });
    setDrafts(d);
  }).catch((e) => {
    setData(null);
    setErr(formatApiError(e.response?.data?.detail));
  });
  useEffect(() => {
    api.get("/admin/gbp/status").then((r) => {
      setSt(r.data);
      if (r.data?.connected) loadReviews();
    }).catch(() => setSt({ connected: false }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    try {
      const { data } = await api.get("/admin/gbp/connect");
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnect = async () => {
    try {
      await api.delete("/admin/gbp/disconnect");
      toast.success("Avis Google déconnectés");
      setSt({ connected: false }); setData(null); setErr("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reply = async (rv) => {
    const comment = (drafts[rv.review_id] || "").trim();
    if (comment.length < 2) { toast.error("La réponse doit contenir au moins 2 caractères"); return; }
    setBusy(rv.review_id);
    try {
      await api.put("/admin/gbp/reply", { name: rv.name, comment });
      toast.success("Réponse publiée sur Google — visible sur la recherche et Maps d'ici quelques minutes");
      loadReviews();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-6 mb-8" data-testid="admin-gbp-reviews">
      <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Avis Google — répondre directement sur Google</p>
      {!st ? (
        <Loader2 className="w-4 h-4 animate-spin text-gold" />
      ) : !st.connected ? (
        <>
          <p className="text-xs text-ivory/55 mb-3">
            Connectez la fiche Google de l'entreprise pour voir TOUS les avis (pas seulement les 5 étoiles
            ci-dessous) et y répondre publiquement depuis ici — les réponses partent directement sur Google.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <button onClick={connect} data-testid="gbp-connect-btn"
              className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
              Connecter les avis Google
            </button>
            <button onClick={() => setGuide((g) => !g)} data-testid="gbp-guide-toggle"
              className="text-xs text-gold hover:underline underline-offset-4">
              {guide ? "Masquer la marche à suivre" : "Google refuse la connexion ? Voir la marche à suivre"}
            </button>
          </div>
          {/* Marche à suivre d'approbation (demande du propriétaire) : l'API avis
              exige l'approbation du projet Google Cloud — étapes + texte prêt à coller */}
          {guide && (
            <div className="mt-2 mb-2 rounded-lg border border-amber-400/25 bg-amber-400/5 p-4" data-testid="gbp-guide">
              <p className="text-xs font-semibold text-amber-300 mb-2">Marche à suivre (une seule fois, dans votre console Google Cloud) :</p>
              <ol className="text-xs text-ivory/70 space-y-1.5 list-decimal list-inside mb-3">
                <li>Ouvrez <span className="text-gold">console.cloud.google.com</span> et choisissez le MÊME projet que celui de Search Console.</li>
                <li>« API et services » → « Bibliothèque » : activez <em>My Business API</em>, <em>My Business Account Management API</em> et <em>My Business Business Information API</em>.</li>
                <li>Remplissez le formulaire d'accès Google (anglais) : <span className="text-gold">developers.google.com/my-business/content/prereqs</span> → « Request access ». Texte prêt à coller ci-dessous.</li>
                <li>« Écran de consentement OAuth » → ajoutez la portée <span className="text-gold">…/auth/business.manage</span>.</li>
                <li>« Identifiants » → votre client OAuth existant → « URI de redirection autorisées » → ajoutez <span className="text-gold">https://mrtrophee.ca/api/admin/gbp/callback</span>.</li>
                <li>Au courriel d'approbation de Google (quelques jours ouvrables) : revenez ici et cliquez « Connecter les avis Google ».</li>
              </ol>
              <p className="text-[11px] uppercase tracking-widest text-ivory/50 mb-1">Texte à coller dans le formulaire Google :</p>
              <div className="relative">
                <pre className="text-[11px] text-ivory/65 bg-ink border border-ivory/15 rounded-lg p-3 whitespace-pre-wrap" data-testid="gbp-guide-text">Mr Trophée, a trophy and awards shop in Châteauguay (Québec, Canada), wants to read its own Google Business Profile reviews and post owner replies from its private admin panel (mrtrophee.ca/admin). Single business, owner's own Google account only. Reviews are displayed only to the store administrator and are never shared, sold or republished elsewhere.</pre>
                <button type="button" data-testid="gbp-guide-copy"
                  onClick={() => { navigator.clipboard.writeText("Mr Trophée, a trophy and awards shop in Châteauguay (Québec, Canada), wants to read its own Google Business Profile reviews and post owner replies from its private admin panel (mrtrophee.ca/admin). Single business, owner's own Google account only. Reviews are displayed only to the store administrator and are never shared, sold or republished elsewhere."); toast.success("Texte copié — collez-le dans le formulaire Google"); }}
                  className="absolute top-2 right-2 text-[11px] px-2.5 py-1 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors">
                  Copier
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span data-testid="gbp-status-label" className="text-xs uppercase tracking-widest text-emerald-300">
              Connecté{st.location_title ? ` — ${st.location_title}` : ""}{st.email ? ` (${st.email})` : ""}
            </span>
            <button onClick={disconnect} data-testid="gbp-disconnect-btn"
              className="px-3 py-1.5 rounded-full text-xs border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors">
              Déconnecter
            </button>
          </div>
          {err ? (
            <p className="text-amber-300 text-xs" data-testid="gbp-error">{err}</p>
          ) : !data ? (
            <Loader2 className="w-4 h-4 animate-spin text-gold" />
          ) : data.reviews.length === 0 ? (
            <p className="text-ivory/45 text-sm" data-testid="gbp-empty">Aucun avis sur la fiche pour le moment.</p>
          ) : (
            <ul className="space-y-3" data-testid="gbp-list">
              {data.reviews.map((rv) => (
                <li key={rv.review_id} data-testid={`gbp-review-${rv.review_id}`}
                  className={`border rounded-lg p-4 ${rv.reply ? "border-ivory/10" : "border-amber-400/40 bg-amber-400/5"}`}>
                  <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                    <span className="flex gap-0.5">
                      {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < rv.rating ? "fill-gold text-gold" : "text-gold/30"}`} />)}
                    </span>
                    <span className="text-sm font-semibold text-champagne">{rv.author}</span>
                    <span className="text-[11px] text-ivory/40">
                      {rv.publish_time && new Date(rv.publish_time).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" })}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider ml-auto ${rv.reply ? "text-emerald-300" : "text-amber-400"}`}>
                      {rv.reply ? "Répondu sur Google" : "Sans réponse"}
                    </span>
                  </div>
                  {rv.text && <p className="text-sm text-ivory/75 mb-3">{rv.text}</p>}
                  {rv.reply && (
                    <p className="text-xs text-emerald-200/80 border-l-2 border-emerald-400/40 pl-3 mb-3"
                      data-testid={`gbp-reply-current-${rv.review_id}`}>
                      Votre réponse : {rv.reply}
                    </p>
                  )}
                  <div className="flex gap-2 items-start">
                    <textarea rows={2} maxLength={4096} value={drafts[rv.review_id] || ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [rv.review_id]: e.target.value }))}
                      placeholder="Merci pour votre confiance… (publiée publiquement sur Google)"
                      data-testid={`gbp-reply-input-${rv.review_id}`}
                      className={`${INPUT} resize-y flex-1`} />
                    <button onClick={() => reply(rv)} disabled={busy === rv.review_id}
                      data-testid={`gbp-reply-save-${rv.review_id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs bg-gold text-ink font-semibold hover:bg-champagne transition-colors disabled:opacity-50 shrink-0">
                      {busy === rv.review_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      {rv.reply ? "Mettre à jour" : "Publier sur Google"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ---- Témoignages clients reçus contre le rabais −5 % (demande du propriétaire,
// 2026-10-07) : modération — rien ne s'affiche sur le site sans « Publier » ----
function AdminTemoignages() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/temoignages").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const moderer = async (t, publie) => {
    setBusy(t.id);
    try {
      await api.patch(`/admin/temoignages/${t.id}`, { publie });
      toast.success(publie ? "Témoignage publié sur la page d'accueil" : "Témoignage retiré du site");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };
  const supprimer = async (t) => {
    if (!window.confirm(`Supprimer le témoignage de « ${t.nom} » ? (son code rabais reste valide)`)) return;
    setBusy(t.id);
    try {
      await api.delete(`/admin/temoignages/${t.id}`);
      toast.success("Témoignage supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };

  if (!items) return null;
  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-6 mb-8" data-testid="admin-temoignages">
      <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Témoignages clients (rabais −5 %)</p>
      <p className="text-xs text-ivory/55 mb-4">
        Reçus via le popup du panier. Rien ne s'affiche sur le site sans « Publier ».
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-ivory/45" data-testid="temoignages-empty">Aucun témoignage reçu pour l'instant.</p>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} className="bg-ink border border-ivory/10 rounded-lg p-4" data-testid={`temoignage-admin-${t.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-champagne text-sm">{t.nom}
                    <span className="ml-2 text-[10px] font-mono text-ivory/40">{t.code}</span>
                    {t.publie && <span className="ml-2 text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 rounded-full px-2 py-0.5">publié</span>}
                  </p>
                  <p className="text-xs text-ivory/50 mb-2">{new Date(t.created_at).toLocaleDateString("fr-CA", { timeZone: "America/Montreal", year: "numeric", month: "long", day: "numeric" })}</p>
                  <p className="text-sm text-ivory/75 leading-relaxed">« {t.texte} »</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => moderer(t, !t.publie)} disabled={busy === t.id}
                    data-testid={`temoignage-publier-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${t.publie ? "border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold" : "bg-gold text-ink hover:bg-champagne"}`}>
                    {busy === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {t.publie ? "Dépublier" : "Publier"}
                  </button>
                  <button onClick={() => supprimer(t)} disabled={busy === t.id}
                    data-testid={`temoignage-supprimer-${t.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors disabled:opacity-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminReviews() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/reviews")
    .then((r) => {
      setData(r.data);
      const d = {};
      (r.data?.reviews || []).forEach((rev) => {
        d[rev.review_key] = { text: rev.reply_fr || "", text_en: rev.reply_en || "" };
      });
      setDrafts(d);
    })
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const draft = drafts[key];
    if (!draft?.text || draft.text.trim().length < 2) {
      toast.error("La réponse française doit contenir au moins 2 caractères");
      return;
    }
    setBusy(`save-${key}`);
    try {
      await api.put("/admin/reviews/reply", { review_key: key, text: draft.text, text_en: draft.text_en || "" });
      toast.success("Réponse publiée sur la page d'accueil");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async (key) => {
    setBusy(`del-${key}`);
    try {
      await api.delete("/admin/reviews/reply", { params: { review_key: key } });
      toast.success("Réponse retirée de la page d'accueil");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return <div className="flex justify-center py-20" data-testid="admin-reviews-loading"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  }

  if (data.source !== "google" || !data.reviews?.length) {
    return (
      <div>
        <AdminReviewKit />
        <AdminGoogleBusinessReviews />
        <AdminReviewStats />
        <AdminProductReviews />
        <AdminTemoignages />
        <p data-testid="admin-reviews-unavailable" className="text-ivory/60 text-sm border border-ivory/15 rounded-xl p-6">
          Les avis Google sont indisponibles pour le moment. Vérifiez la clé Google Places dans les paramètres du serveur.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews" className="space-y-6">
      <AdminReviewKit />
      <AdminGoogleBusinessReviews />
      <AdminReviewStats />
      <AdminProductReviews />
      <AdminTemoignages />
      <p className="text-ivory/60 text-sm">
        {data.business_name} — {data.rating?.toLocaleString("fr-CA", { minimumFractionDigits: 1 })} / 5
        ({data.reviews.length} avis 5 étoiles affichés sur {data.user_rating_count} avis Google).
        Vos réponses s'affichent publiquement sous chaque avis sur la page d'accueil.
      </p>
      {data.reviews.map((rev, i) => {
        const draft = drafts[rev.review_key] || { text: "", text_en: "" };
        const hasReply = Boolean(rev.reply_fr);
        return (
          <div key={rev.review_key} data-testid={`admin-review-${i}`}
            className="bg-surface border border-ivory/15 rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                {rev.author_photo ? (
                  <img src={rev.author_photo} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center font-serif text-gold">{rev.author?.[0] || "?"}</span>
                )}
                <div className="min-w-0">
                  <ExpandableText text={rev.author} testId={`admin-review-author-${i}`} className="block font-semibold text-champagne" />
                  <div className="flex gap-0.5 my-0.5">
                    {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < Math.round(rev.rating) ? "fill-gold text-gold" : "text-gold/30"}`} />)}
                  </div>
                  <p className="text-xs text-ivory/70">
                    {rev.publish_time && new Date(rev.publish_time).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>
              </div>
              {hasReply && (
                <span data-testid={`admin-review-replied-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 bg-emerald-400/10">
                  Réponse publiée
                </span>
              )}
            </div>
            <blockquote className="text-ivory/70 text-sm leading-relaxed border-l-2 border-gold/30 pl-4" data-testid={`admin-review-text-${i}`}>
              {rev.text}
            </blockquote>
            <div className="grid md:grid-cols-2 gap-3">
              <label>
                <span className={LABEL}>Votre réponse (français)</span>
                <textarea data-testid={`admin-review-reply-fr-${i}`} rows={3} maxLength={2000}
                  value={draft.text}
                  onChange={(e) => setDrafts((d) => ({ ...d, [rev.review_key]: { ...draft, text: e.target.value } }))}
                  placeholder="Merci pour votre confiance…" className={`${INPUT} resize-y`} />
              </label>
              <label>
                <span className={LABEL}>Votre réponse (anglais — optionnel)</span>
                <textarea data-testid={`admin-review-reply-en-${i}`} rows={3} maxLength={2000}
                  value={draft.text_en}
                  onChange={(e) => setDrafts((d) => ({ ...d, [rev.review_key]: { ...draft, text_en: e.target.value } }))}
                  placeholder="Thank you for your trust… (sinon la version française s'affiche)" className={`${INPUT} resize-y`} />
              </label>
            </div>
            <div className="flex gap-3 justify-end">
              {hasReply && (
                <button type="button" data-testid={`admin-review-reply-delete-${i}`} onClick={() => remove(rev.review_key)}
                  disabled={busy === `del-${rev.review_key}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors disabled:opacity-50">
                  {busy === `del-${rev.review_key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Retirer
                </button>
              )}
              <button type="button" data-testid={`admin-review-reply-save-${i}`} onClick={() => save(rev.review_key)}
                disabled={busy === `save-${rev.review_key}`}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors disabled:opacity-50">
                {busy === `save-${rev.review_key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {hasReply ? "Mettre à jour" : "Publier"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
